import { fetchApi } from '@/lib/api';
import type { GithubActivity } from '@shared/github-activity';
import type { ApprovalRequest, ApproveDecision } from '@shared/approval-request';
import type { FactoryAnswer } from '@shared/factory-questions';
import type { PatchCheck, PatchRequest, PatchRequestListResponse } from '@shared/patch-request';
import { PROJECT_HEADER } from '@/lib/factory-request';
import type {
  TaskAttachment,
  TaskBoard,
  TaskBoardMeta,
  TaskBoardsOfUser,
  TaskFinishedOfProject,
  TaskCard,
  TaskColumn,
  TaskContextRef,
  TaskContextSearchHit,
  TaskCreateInput,
  TaskDetail,
  TaskFinishedPage,
  TaskLabel,
  TaskLabelColor,
  TaskMergeInput,
  TaskMergeResult,
  TaskMoveInput,
  TaskNotification,
  TaskParentChoice,
  TaskRef,
  TaskPrHandOffKind,
  TaskType,
  TaskTypeField,
  TaskUpdateInput,
  TaskUpdateResponse,
  SubtaskOrderSuggestion,
} from '@shared/types/tasks';
import { TASK_BOARD_DONE_LIMIT, type PrMergeBatch } from '@shared/types/tasks';
import type { TaskUpstreamPeople } from '@shared/types/task-sources';
import type { TaskPrsLiveResponse } from '@shared/github-pr';
import type { SuggestionChat } from '@shared/suggestion-chat';

const base = '/tasks';

/**
 * Tasks created optimistically (with an id chosen here) are usable before the server has them:
 * every request about that task waits for its create to finish first.
 */
const creating = new Map<string, Promise<unknown>>();

async function created(taskId: string): Promise<void> {
  await creating.get(taskId)?.catch(() => undefined);
}

function json(method: string, body?: unknown): RequestInit {
  return { method, body: body === undefined ? undefined : JSON.stringify(body) };
}

/** A request for another project than the tab's (the master board, T-26): its id or slug in the header. */
function forProject(init: RequestInit, project: string | undefined): RequestInit {
  return project ? { ...init, headers: { ...(init.headers as Record<string, string> | undefined), [PROJECT_HEADER]: project } } : init;
}

export function scopeParam(scope: TaskContextRef[]): string {
  return scope.map((ref) => `${ref.type}:${encodeURIComponent(ref.id)}`).join(',');
}

/**
 * `done`: how many finished tasks the Board loads (Show older raises it). A scoped board has
 * them all. `project` names another project than the tab's (the master board, T-26).
 */
export function getTaskBoard(
  opts: { scope?: TaskContextRef[]; archived?: boolean; orphaned?: boolean; project?: string; signal?: AbortSignal } = {},
  done?: number
) {
  const params = new URLSearchParams();
  if (opts.scope?.length) params.set('scope', scopeParam(opts.scope));
  if (opts.archived) params.set('archived', '1');
  // T-71: orphaned sub-tasks too, those off the Board included.
  if (opts.orphaned) params.set('orphaned', '1');
  if (done && done !== TASK_BOARD_DONE_LIMIT && !opts.scope?.length) params.set('done', String(done));
  const qs = params.toString();
  return fetchApi<TaskBoard>(`${base}/board${qs ? `?${qs}` : ''}`, forProject({ signal: opts.signal }, opts.project));
}

/** The Board without its tasks (columns, labels, types, people, `#tags`): use `useTaskMeta`, which caches it. */
export function getTaskMeta(opts: { project?: string; signal?: AbortSignal } = {}) {
  return fetchApi<TaskBoardMeta>(`${base}/meta`, forProject({ signal: opts.signal }, opts.project));
}

/**
 * The master board (T-26): every visible project's Board in one answer; `done` per project, as on a
 * Board. Projects the person leaves off their board come without one, unless `show` names them.
 */
export function getAllTaskBoards(opts: { archived?: boolean; show?: readonly string[]; signal?: AbortSignal } = {}, done?: number) {
  const params = new URLSearchParams();
  if (opts.archived) params.set('archived', '1');
  if (done && done !== TASK_BOARD_DONE_LIMIT) params.set('done', String(done));
  if (opts.show?.length) params.set('show', opts.show.join(','));
  const qs = params.toString();
  return fetchApi<TaskBoardsOfUser>(`/tasks-all/board${qs ? `?${qs}` : ''}`, { signal: opts.signal });
}

/** The master board's search of older finished tasks, in every project it loads. */
export function getAllFinishedTasks(opts: { archived?: boolean; q: string; show?: readonly string[] }) {
  const params = new URLSearchParams({ q: opts.q });
  if (opts.archived) params.set('archived', '1');
  if (opts.show?.length) params.set('show', opts.show.join(','));
  return fetchApi<{ projects: TaskFinishedOfProject[] }>(`/tasks-all/finished?${params.toString()}`);
}

/** Replaces the projects the caller leaves off their master board; answers the list as stored. */
export function setHiddenBoardProjects(projectIds: readonly string[]) {
  return fetchApi<{ project_ids: string[] }>('/tasks-all/hidden', { method: 'PUT', body: JSON.stringify({ project_ids: projectIds }) });
}

/** Older finished tasks the Board's search finds. */
export function getFinishedTasks(opts: { archived?: boolean; orphaned?: boolean; q: string }) {
  const params = new URLSearchParams({ q: opts.q });
  if (opts.archived) params.set('archived', '1');
  if (opts.orphaned) params.set('orphaned', '1');
  return fetchApi<TaskFinishedPage>(`${base}/finished?${params.toString()}`);
}

/** Fresh copies of some cards (and the cards Waiting on them), for Boards already loaded. */
export function getTaskCards(taskIds: string[]) {
  return fetchApi<Pick<TaskBoard, 'tasks' | 'tags' | 'members'>>(`${base}/cards?ids=${taskIds.join(',')}`);
}

/** `project`: the task's project, when it isn't the tab's (the Inbox). */
export async function getTask(taskId: string, opts: { project?: string; signal?: AbortSignal } = {}) {
  await created(taskId);
  return fetchApi<{ task: TaskDetail }>(`${base}/${taskId}`, forProject({ signal: opts.signal }, opts.project));
}

export function createTask(input: TaskCreateInput) {
  const request = (input.label_ids?.length ? labelsCreated() : Promise.resolve()).then(() => fetchApi<{ task: TaskCard }>(base, json('POST', input)));
  if (input.id) {
    const id = input.id;
    creating.set(id, request);
    void request.finally(() => creating.delete(id));
  }
  return request;
}

/** The last edit sent for each task. Edits go one at a time so a quick assign-then-unassign lands in order. */
const updating = new Map<string, Promise<unknown>>();

/** Summarise again (T-17): a new AI summary now. Answers with the card, summary included. */
export function summariseTask(taskId: string) {
  return fetchApi<{ task: TaskCard }>(`${base}/${taskId}/summary`, { method: 'POST' });
}

export function updateTask(taskId: string, input: TaskUpdateInput) {
  const previous = updating.get(taskId);
  const request = (async () => {
    await previous?.catch(() => undefined);
    await created(taskId);
    if (input.label_ids) await labelsCreated();
    return fetchApi<TaskUpdateResponse>(`${base}/${taskId}`, json('PATCH', input));
  })();
  updating.set(taskId, request);
  void request.finally(() => {
    if (updating.get(taskId) === request) updating.delete(taskId);
  });
  return request;
}

/** `project`: the task's project, when it isn't the tab's (the master board, T-26). */
export async function moveTask(taskId: string, input: TaskMoveInput, project?: string) {
  await created(taskId);
  return fetchApi<{ task: TaskCard }>(`${base}/${taskId}/move`, forProject(json('POST', input), project));
}

export async function addTaskContexts(taskId: string, contexts: Array<TaskContextRef & { title?: string }>) {
  await created(taskId);
  return fetchApi<{ task: TaskCard }>(`${base}/${taskId}/contexts`, json('POST', { contexts }));
}

export async function removeTaskContext(taskId: string, ref: TaskContextRef) {
  await created(taskId);
  const params = new URLSearchParams({ type: ref.type, id: ref.id });
  return fetchApi<{ task: TaskCard }>(`${base}/${taskId}/contexts?${params}`, { method: 'DELETE' });
}

/** `id` (chosen here) lets an optimistic message match the saved one, and makes retries safe. */
/** `postUpstream` (T-15): also post it on a mirrored task's source; `upstream_error` says why it couldn't. */
export async function postTaskChat(taskId: string, body: string, attachmentIds: string[] = [], id?: string, postUpstream = false, answersFor?: string) {
  await created(taskId);
  return fetchApi<{ event_id: string; upstream_error?: string | null }>(
    `${base}/${taskId}/chat`,
    json('POST', { id, body, attachment_ids: attachmentIds, ...(postUpstream ? { post_upstream: true } : {}), ...(answersFor ? { answers_for: answersFor } : {}) })
  );
}

export async function editTaskChat(taskId: string, eventId: string, body: string) {
  await created(taskId);
  return fetchApi<{ ok: true }>(`${base}/${taskId}/chat/${eventId}`, json('PATCH', { body }));
}

export async function deleteTaskChat(taskId: string, eventId: string, project?: string) {
  await created(taskId);
  return fetchApi<{ ok: true }>(`${base}/${taskId}/chat/${eventId}`, forProject({ method: 'DELETE' }, project));
}

export async function uploadTaskAttachment(taskId: string, file: File) {
  await created(taskId);
  const form = new FormData();
  form.append('file', file, file.name || 'pasted-image.png');
  return fetchApi<{ attachment: TaskAttachment }>(`${base}/${taskId}/attachments`, { method: 'POST', body: form });
}

export async function deleteTaskAttachment(taskId: string, attachmentId: string) {
  await created(taskId);
  return fetchApi<{ ok: true }>(`${base}/${taskId}/attachments/${attachmentId}`, { method: 'DELETE' });
}

export async function setTaskFollowing(taskId: string, following: boolean) {
  await created(taskId);
  return fetchApi<{ ok: true }>(`${base}/${taskId}/follow`, json('POST', { following }));
}

export function searchTaskContexts(q: string) {
  return fetchApi<{ results: TaskContextSearchHit[] }>(`${base}/context-search?q=${encodeURIComponent(q)}`);
}

export function getTaskNotifications() {
  return fetchApi<{ notifications: TaskNotification[]; unread: number }>(`${base}/notifications`);
}

export function markTaskNotificationsRead(opts: { ids?: string[]; task_id?: string } = {}) {
  return fetchApi<{ ok: true }>(`${base}/notifications/read`, json('POST', opts));
}

export function createTaskColumn(input: { name: string; is_done?: boolean }) {
  return fetchApi<{ column: TaskColumn }>(`${base}/columns`, json('POST', input));
}

export function updateTaskColumn(columnId: string, input: { name?: string; is_done?: boolean; asks_ai?: boolean }) {
  return fetchApi<{ ok: true }>(`${base}/columns/${columnId}`, json('PATCH', input));
}

export function reorderTaskColumns(ids: string[]) {
  return fetchApi<{ ok: true }>(`${base}/columns/reorder`, json('POST', { ids }));
}

export function deleteTaskColumn(columnId: string) {
  return fetchApi<{ ok: true }>(`${base}/columns/${columnId}`, { method: 'DELETE' });
}

/** Labels being created; saving a task's labels waits for them so the new label isn't dropped. */
const creatingLabels = new Set<Promise<unknown>>();

async function labelsCreated(): Promise<void> {
  await Promise.all([...creatingLabels].map((p) => p.catch(() => undefined)));
}

export function createTaskLabel(input: { id?: string; name: string; color: TaskLabelColor }) {
  const request = fetchApi<{ label: TaskLabel }>(`${base}/labels`, json('POST', input));
  creatingLabels.add(request);
  void request.finally(() => creatingLabels.delete(request));
  return request;
}

export function updateTaskLabel(labelId: string, input: { name?: string; color?: TaskLabelColor }) {
  return fetchApi<{ ok: true }>(`${base}/labels/${labelId}`, json('PATCH', input));
}

export function deleteTaskLabel(labelId: string) {
  return fetchApi<{ ok: true }>(`${base}/labels/${labelId}`, { method: 'DELETE' });
}

/* Types: name, colour and which fields each shows. */

export function createTaskType(input: { name: string; color: TaskLabelColor; fields: TaskTypeField[] }) {
  return fetchApi<{ type: TaskType }>(`${base}/types`, json('POST', input));
}

export function updateTaskType(typeId: string, input: { name?: string; color?: TaskLabelColor; fields?: TaskTypeField[] }) {
  return fetchApi<{ ok: true }>(`${base}/types/${typeId}`, json('PATCH', input));
}

export function reorderTaskTypes(ids: string[]) {
  return fetchApi<{ ok: true }>(`${base}/types/reorder`, json('POST', { ids }));
}

export function deleteTaskType(typeId: string) {
  return fetchApi<{ ok: true }>(`${base}/types/${typeId}`, { method: 'DELETE' });
}

/** The Attachments' new order: the top image or video is the task's cover. */
export function reorderTaskAttachments(taskId: string, ids: string[]) {
  return fetchApi<{ ok: true }>(`${base}/${taskId}/attachments/order`, json('POST', { ids }));
}

/** T-42: the task's sub-tasks in this order. */
export function reorderTaskSubtasks(taskId: string, ids: string[]) {
  return fetchApi<{ ok: true }>(`${base}/${taskId}/subtasks/order`, json('POST', { ids }));
}

/** SF-T-271: Suggest the order: the AI's suggested dependencies and breaks for its sub-tasks, kept on the task (not applied). */
export function suggestSubtaskOrder(taskId: string) {
  return fetchApi<{ suggestion: SubtaskOrderSuggestion }>(`${base}/${taskId}/subtasks/suggest-order`, json('POST', {}));
}

/** SF-T-271: apply every change in the suggestion `id` (409 SUBTASK_ORDER_STALE once the sub-tasks changed since). */
export function acceptSubtaskOrder(taskId: string, id: string) {
  return fetchApi<{ ok: true; changed: string[] }>(`${base}/${taskId}/subtasks/suggest-order/accept`, json('POST', { id }));
}

/** SF-T-271: dismiss the suggestion `id`: nothing changes. */
export function dismissSubtaskOrder(taskId: string, id: string) {
  return fetchApi<{ ok: true }>(`${base}/${taskId}/subtasks/suggest-order/dismiss`, json('POST', { id }));
}

/** SF-T-132: put the task under that parent (the way back from promoteSubtaskToTask). */
export async function makeTaskSubtaskOf(taskId: string, parentTaskId: string) {
  await created(taskId);
  return fetchApi<{ parent_task_id: string }>(`${base}/${taskId}/make-subtask`, json('POST', { parent_task_id: parentTaskId }));
}

/** SF-T-147: a sub-task becomes a task of its own, off its parent and on the Board. */
export async function promoteSubtaskToTask(taskId: string) {
  await created(taskId);
  return fetchApi<{ parent_task_id: string }>(`${base}/${taskId}/promote-subtask`, json('POST', {}));
}

/** SF-T-103: merge picked tasks into one with sub-tasks: a picked one as the parent, or a new one. `project`: not the tab's (the master board). */
export function mergeTasks(input: TaskMergeInput, project?: string) {
  return fetchApi<TaskMergeResult>(`${base}/merge`, forProject(json('POST', input), project));
}

/** T-50: the description a change (its `described` event) replaced, with mention markup. */
export function getDescriptionBefore(taskId: string, eventId: string, opts: { signal?: AbortSignal } = {}) {
  return fetchApi<{ description: string }>(`${base}/${taskId}/events/${eventId}/description-before`, { signal: opts.signal });
}

/** SF-T-202: the repo's open PRs and latest Actions runs with their tasks, for the Board header. `fresh`: read GitHub first unless it just did. */
export function getGithubActivity(opts: { fresh?: boolean; signal?: AbortSignal } = {}) {
  return fetchApi<GithubActivity>(`${base}/github-activity${opts.fresh ? '?fresh=1' : ''}`, { signal: opts.signal });
}

/** A task's pull requests as GitHub sees them now (T-3). */
export function getTaskPrsLive(taskId: string, opts: { signal?: AbortSignal; project?: string } = {}) {
  return fetchApi<TaskPrsLiveResponse>(`${base}/${taskId}/prs`, forProject({ signal: opts.signal }, opts.project));
}

/** Merge one of the task's pull requests on GitHub (maintainers; T-3). Answers the updated card. */
export function mergeTaskPr(taskId: string, url: string) {
  return fetchApi<{ task: TaskCard; sha: string | null }>(`${base}/${taskId}/prs/merge`, json('POST', { url }));
}

/**
 * SF-T-198: merge picked tasks' open pull requests one at a time on the server, in this order (maintainers). `project`: not the tab's (the master board).
 * Answers the batch as queued; its steps come live (`pr_merge_batch`).
 */
export function startPrMergeBatch(items: { task_id: string; url: string }[], project?: string) {
  return fetchApi<{ batch: PrMergeBatch }>(`${base}/merge-prs`, forProject(json('POST', { items }), project));
}

/** SF-T-198: your running Merge PRs batches and those finished in the last half hour, newest first, for the Board to offer again. */
export function listPrMergeBatches(opts: { signal?: AbortSignal } = {}) {
  return fetchApi<{ batches: PrMergeBatch[] }>(`${base}/merge-prs`, { signal: opts.signal });
}

/** SF-T-198: a Merge PRs batch as it stands (after a reconnect). */
export function getPrMergeBatch(batchId: string, opts: { signal?: AbortSignal; project?: string } = {}) {
  return fetchApi<{ batch: PrMergeBatch }>(`${base}/merge-prs/${batchId}`, forProject({ signal: opts.signal }, opts.project));
}

/** SF-T-198: stop a running Merge PRs batch: the PRs it hasn't reached are skipped. Answers the batch. */
export function stopPrMergeBatch(batchId: string, project?: string) {
  return fetchApi<{ batch: PrMergeBatch }>(`${base}/merge-prs/${batchId}/stop`, forProject(json('POST', {}), project));
}

/** SF-T-79: hand a pull request that conflicts with its base to the AI builder to fix (maintainers). Answers the updated card. */
export function fixTaskPrConflicts(taskId: string, url: string) {
  return fetchApi<{ task: TaskCard }>(`${base}/${taskId}/prs/fix-conflicts`, json('POST', { url }));
}

/**
 * SF-T-183: hand a pull request to the AI builder to merge its base in and run `pnpm db:rebase` (maintainers):
 * one adding migrations while main has newer ones. Answers the updated card and why it was handed over.
 */
export function updateTaskPr(taskId: string, url: string) {
  return fetchApi<{ task: TaskCard; kind: TaskPrHandOffKind }>(`${base}/${taskId}/prs/update`, json('POST', { url }));
}

/** SF-T-177: the permission requests the task's runs waited on in T3 Code, the open ones first. */
export function getTaskApprovalRequests(taskId: string, opts: { signal?: AbortSignal } = {}) {
  return fetchApi<{ approval_requests: ApprovalRequest[] }>(`${base}/${taskId}/approval-requests`, { signal: opts.signal });
}

/** SF-T-177: deny one (RUNS_START): the agent's waiting turn restarts with the note. */
export function denyTaskApprovalRequest(taskId: string, requestId: string, note: string) {
  return fetchApi<{ approval_request: ApprovalRequest }>(`${base}/${taskId}/approval-requests/${requestId}/deny`, json('POST', { note }));
}

/** SF-T-225: approve one (RUNS_START): Approve, or Approve for session; the run sends it to T3 and its agent carries on. */
export function approveTaskApprovalRequest(taskId: string, requestId: string, decision: ApproveDecision, project?: string) {
  return fetchApi<{ approval_request: ApprovalRequest }>(`${base}/${taskId}/approval-requests/${requestId}/approve`, forProject(json('POST', { decision }), project));
}

/** SF-T-225: T3's own Decline with a message (RUNS_START): the agent carries on in the same turn without it. */
export function declineTaskApprovalRequest(taskId: string, requestId: string, note: string) {
  return fetchApi<{ approval_request: ApprovalRequest }>(`${base}/${taskId}/approval-requests/${requestId}/decline`, json('POST', { note }));
}

/** SF-T-177: the AI's explanation of one, made afresh. */
/** SF-T-177: answers a run's agent's own T3 question (RUNS_START): the run sends them through T3, and the agent carries on. */
export function answerTaskApprovalQuestion(taskId: string, requestId: string, answers: Record<string, FactoryAnswer | undefined>) {
  return fetchApi<{ approval_request: ApprovalRequest }>(`${base}/${taskId}/approval-requests/${requestId}/answer`, json('POST', { answers }));
}

export function explainTaskApprovalRequest(taskId: string, requestId: string) {
  return fetchApi<{ approval_request: ApprovalRequest }>(`${base}/${taskId}/approval-requests/${requestId}/explain`, json('POST', {}));
}

/** T-57: the task's patch requests from the coding agent, newest first. */
export function getTaskPatchRequests(taskId: string, opts: { signal?: AbortSignal } = {}) {
  return fetchApi<PatchRequestListResponse>(`${base}/${taskId}/patch-requests`, { signal: opts.signal });
}

/** Whether a patch request still applies to its branch: the machine checks, changing nothing. */
export function checkTaskPatchRequest(taskId: string, requestId: string, opts: { signal?: AbortSignal } = {}) {
  return fetchApi<{ check: PatchCheck }>(`${base}/${taskId}/patch-requests/${requestId}/check`, { signal: opts.signal });
}

/** Apply a patch request (factory admins): the machine commits and pushes it in the run's worktree, then the agent is told. */
export function applyTaskPatchRequest(taskId: string, requestId: string, opts: { signal?: AbortSignal } = {}) {
  return fetchApi<{ patch_request: PatchRequest }>(`${base}/${taskId}/patch-requests/${requestId}/apply`, { ...json('POST', {}), signal: opts.signal });
}

/** Reject a patch request (factory admins), with an optional note for the agent. */
export function rejectTaskPatchRequest(taskId: string, requestId: string, note: string) {
  return fetchApi<{ patch_request: PatchRequest }>(`${base}/${taskId}/patch-requests/${requestId}/reject`, json('POST', { note }));
}

/** SF-T-107: apply every waiting patch request (`ids`, exactly the task's waiting ones) as one commit and one push. */
export function applyAllTaskPatchRequests(taskId: string, ids: string[], opts: { signal?: AbortSignal } = {}) {
  return fetchApi<{ patch_requests: PatchRequest[] }>(`${base}/${taskId}/patch-requests/apply-all`, { ...json('POST', { ids }), signal: opts.signal });
}

/** SF-T-107: reject every waiting patch request (`ids`) with one note for the agent. */
export function rejectAllTaskPatchRequests(taskId: string, ids: string[], note: string) {
  return fetchApi<{ patch_requests: PatchRequest[] }>(`${base}/${taskId}/patch-requests/reject-all`, json('POST', { ids, note }));
}

/** Ask the AI to explain the patch again. */
export function explainTaskPatchRequest(taskId: string, requestId: string) {
  return fetchApi<{ patch_request: PatchRequest }>(`${base}/${taskId}/patch-requests/${requestId}/explain`, json('POST', {}));
}

/**
 * T-14: decide the AI builder's suggestions in the factory message `eventId`; answers the decisions
 * message and the tasks made. `project`: the task's project, when it isn't the tab's (the Inbox).
 */
export async function decideTaskSuggestions(taskId: string, eventId: string, decisions: Array<{ number: number; action: string; note: string }>, project?: string) {
  await created(taskId);
  return fetchApi<{ event_id: string; tasks: Array<{ number: number; task_id: string; ref: string }> }>(
    `${base}/${taskId}/suggestions`,
    forProject(json('POST', { event_id: eventId, decisions }), project)
  );
}

/** SF-T-304: the live chats about the AI builder's suggestions in the factory message `eventId`. */
export async function getSuggestionChats(taskId: string, eventId: string, opts: { project?: string; signal?: AbortSignal } = {}) {
  await created(taskId);
  return fetchApi<{ chats: SuggestionChat[] }>(`${base}/${taskId}/suggestions/chats?event_id=${encodeURIComponent(eventId)}`, forProject({ signal: opts.signal }, opts.project));
}

/**
 * SF-T-304: a question about suggestion `number` in the factory message `eventId`: the first starts its chat (in
 * the task's worktree), later ones carry it on. Answers the chat as it is now.
 */
export async function askAboutSuggestion(taskId: string, eventId: string, number: number, message: string, project?: string) {
  await created(taskId);
  return fetchApi<{ chat: SuggestionChat }>(`${base}/${taskId}/suggestions/chats`, forProject(json('POST', { event_id: eventId, number, message }), project));
}

/** SF-T-304: stop suggestion `number`'s chat (its turn, and anything it waits on). Answers the chat as it is now. */
export async function closeSuggestionChat(taskId: string, eventId: string, number: number, project?: string) {
  await created(taskId);
  return fetchApi<{ chat: SuggestionChat }>(`${base}/${taskId}/suggestions/chats/close`, forProject(json('POST', { event_id: eventId, number }), project));
}

/**
 * T-58: send those of the AI builder's questions or suggestions in the factory message `eventId` to the
 * task's source as a comment. `sent` false: nothing went up (`upstream_error` says why); true with an
 * `upstream_error`: it may not have arrived (a timeout), and isn't sent again.
 */
export async function sendDecisionsUpstream(taskId: string, eventId: string, kind: 'questions' | 'suggestions', numbers: number[], mentionId: string | null = null) {
  await created(taskId);
  return fetchApi<{ event_id: string; sent: boolean; upstream_error: string | null }>(
    `${base}/${taskId}/decisions/send-upstream`,
    json('POST', { event_id: eventId, kind, numbers, mention_id: mentionId })
  );
}

/**
 * SF-T-201: send a comment already on the task (anyone's) to the task's source, files and all, tagging
 * `mentionId` there. `sent` false: nothing went up (`upstream_error` says why); with `upstream_error`: it may
 * not have arrived.
 */
export async function sendCommentUpstream(taskId: string, eventId: string, mentionId: string | null = null) {
  await created(taskId);
  return fetchApi<{ sent: boolean; upstream_error: string | null }>(`${base}/${taskId}/chat/${eventId}/send-upstream`, json('POST', { mention_id: mentionId }));
}

/** SF-T-207: make this task (made here) on that source's board; it's mirrored from there after. */
export async function pushTaskUpstream(taskId: string, sourceId: string) {
  await created(taskId);
  return fetchApi<{ ref: string; url: string | null; subtasks_pushed: number; subtasks_left: number }>(`${base}/${taskId}/push-upstream`, json('POST', { source_id: sourceId }));
}

/** SF-T-101: who a send to the task's source can tag: its creator there (ticked to start) and the board's people. */
export async function getUpstreamPeople(taskId: string, opts: { signal?: AbortSignal } = {}) {
  await created(taskId);
  return fetchApi<TaskUpstreamPeople>(`${base}/${taskId}/decisions/upstream-people`, { signal: opts.signal });
}

/**
 * SF-T-62: No thanks on every suggestion still open in the factory messages `eventIds` (the Inbox's
 * Done on suggestions). `project`: the task's project, when it isn't the tab's. `from`: where it was
 * done, which each decision's note says (SF-T-178: 'task' for the task's Dismiss).
 */
export async function declineTaskSuggestions(taskId: string, eventIds: string[], project?: string, from: 'inbox' | 'task' = 'inbox') {
  await created(taskId);
  return fetchApi<{ event_ids: string[]; declined: number; incomplete: boolean }>(
    `${base}/${taskId}/suggestions/decline`,
    forProject(json('POST', { event_ids: eventIds, from }), project)
  );
}

/**
 * SF-T-74: settle the disputed review findings in the factory message `eventId`: for each finding
 * still open, leave it as the builder says or ask the builder to fix it. `project`: the task's project,
 * when it isn't the tab's (the Inbox).
 */
export async function settleTaskDispute(taskId: string, eventId: string, decisions: Array<{ number: number; choice: string; note: string }>, project?: string) {
  await created(taskId);
  return fetchApi<{ event_id: string; settled: boolean }>(`${base}/${taskId}/review/settle`, forProject(json('POST', { event_id: eventId, decisions }), project));
}

/** Add the AI builder's proposed sub-tasks in the factory message `eventId` (`add`: their numbers; the rest are left out). */
export async function addProposedSubtasks(taskId: string, eventId: string, add: number[], project?: string) {
  await created(taskId);
  return fetchApi<{ event_id: string; tasks: Array<{ number: number; task_id: string; ref: string }> }>(
    `${base}/${taskId}/subtask-proposals`,
    forProject(json('POST', { event_id: eventId, add }), project)
  );
}

/** T-14: tasks by number, to link refs like T-31. */
/** SF-T-172: the open top-level tasks `excludeId` can be made a sub-task of (Make sub-task), with no Board. */
export async function getTaskParents(excludeId: string, init: RequestInit = {}) {
  return fetchApi<{ tasks: TaskParentChoice[] }>(`${base}/parents?exclude=${encodeURIComponent(excludeId)}`, init);
}

/** SF-T-241: another project's open top-level tasks (by its slug), for a chat card's parent search. */
export async function getProjectParents(project: string, init: RequestInit = {}) {
  return fetchApi<{ tasks: TaskParentChoice[] }>(`${base}/parents`, forProject(init, project));
}

export async function getTaskRefs(numbers: number[], init: RequestInit = {}) {
  return fetchApi<{ tasks: TaskRef[] }>(`${base}/refs?numbers=${numbers.join(',')}`, init);
}
